import { createTheme } from '@mui/material/styles';

export const createAppTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#0096FF',
        dark: '#0284C7',
        light: '#38B0FF',
        lighter: isDark ? '#1E3A5F' : '#E4F2FD',
      },
      background: {
        default: isDark ? '#0B1120' : '#F8FAFC',
        paper: isDark ? '#1F2937' : '#FFFFFF',
        secondary: isDark ? '#111827' : '#F8FAFC',
        tertiary: isDark ? '#1F2937' : '#F1F5F9',
      },
      text: {
        primary: isDark ? '#FFFFFF' : '#000000',
        secondary: isDark ? '#FFFFFF' : '#1F2937',
        tertiary: isDark ? '#FFFFFF' : '#4B5563',
        muted: isDark ? '#E5E7EB' : '#6B7280',
        inverse: isDark ? '#000000' : '#FFFFFF',
      },
      border: {
        primary: isDark ? '#374151' : '#E5E7EB',
        secondary: isDark ? '#4B5563' : '#D1D5DB',
        tertiary: isDark ? '#6B7280' : '#9CA3AF',
      },
      surface: {
        main: isDark ? '#1F2937' : '#FFFFFF',
        hover: isDark ? '#374151' : '#F8FAFC',
        active: isDark ? '#4B5563' : '#F1F5F9',
      },
      success: {
        main: isDark ? '#10B981' : '#047857',
        light: isDark ? '#064E3B' : '#D1FAE5',
      },
      warning: {
        main: '#F59E0B',
        light: isDark ? '#78350F' : '#FEF3C7',
      },
      error: {
        main: '#EF4444',
        light: isDark ? '#7F1D1D' : '#FEE2E2',
      },
      info: {
        main: '#3B82F6',
        light: isDark ? '#1E3A8A' : '#DBEAFE',
      },
      // Inside createAppTheme(), add this block to the palette object:

      grades: {
        aPlus: '#00C853',
        a: '#2E7D32',
        aMinus: '#43A047',

        bPlus: '#66BB6A',
        b: '#9CCC65',
        bMinus: '#C0CA33',

        cPlus: '#FFD600',
        c: '#FFB300',
        cMinus: '#FF8F00',

        dPlus: '#F4511E',
        d: '#E53935',
        dMinus: '#C62828',

        ePlus: '#BF2121',
        e: '#BF2121',
        eMinus: '#BF2121',

        f: '#B71C1C',
        unknown: '#475569',
      },

      buckets: {
        strongSell: '#C62828',
        sell: '#E53935',
        hold: '#F59E0B',
        buy: '#66BB6A',
        strongBuy: '#00C853',
      },
    },

    /* =========================
       TYPOGRAPHY SYSTEM
    ========================== */
    typography: {
      fontFamily: `'Nunito Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif`,

      title: {
        fontSize: '28px',
        fontWeight: 700,
        lineHeight: 1.3,
        color: isDark ? '#FFFFFF' : '#111827',
      },

      subtitle: {
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: 1.5,
        color: isDark ? '#9CA3AF' : '#4B5563',
      },

      paragraph: {
        fontSize: '17px',
        fontWeight: 400,
        lineHeight: 1.7,
        color: isDark ? '#D1D5DB' : '#374151',
      },

      paragraphV2: {
        fontSize: '15px',
        fontWeight: 400,
        lineHeight: 1.7,
        color: isDark ? '#D1D5DB' : '#374151',
      },

      tableHeader: {
        fontSize: '15px',
        fontWeight: 500,
        lineHeight: 1.5,
        color: isDark ? '#9CA3AF' : '#4B5563',
      },

      chipText: {
        fontSize: '15px',
        fontWeight: 700,
        lineHeight: 1.5,
        color: isDark ? '#9CA3AF' : '#4B5563',
      },

      amount: {
        fontSize: '26px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        color: isDark ? '#10B981' : '#059669',
      },
    },

    /* =========================
       COMPONENT OVERRIDES
    ========================== */
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#0B1120' : '#FFFFFF',
          },
        },
      },

      /* ========= BUTTON THEME ========= */
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 20px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              opacity: 0.9,
            },
          },
          outlined: {
            borderWidth: '1.5px',
          },
        },
      },

      /* ========= TEXTFIELD THEME ========= */
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: isDark ? '#111827' : '#F9FAFB',

              '& fieldset': {
                borderColor: isDark ? '#374151' : '#D1D5DB',
              },
              '&:hover fieldset': {
                borderColor: '#0096FF',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0096FF',
                borderWidth: 2,
              },
            },

            '& .MuiInputLabel-root': {
              color: isDark ? '#9CA3AF' : '#6B7280',
            },

            '& .MuiInputBase-input': {
              fontSize: '14px',
              fontWeight: 500,
            },
          },
        },
      },
    },
  });
};

const theme = createAppTheme('dark');
export default theme;